import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { COMPANY, SERVICES, NAV_LINKS } from "@/lib/data";

const SOCIALS = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Twitter, href: "#", label: "Twitter" },
];

export default function Footer() {
  return (
    <footer className="bg-cloud pt-20" data-testid="footer" itemScope itemType="https://schema.org/LocalBusiness">
      <meta itemProp="name" content="TEKNO ŞURA — AKINSOFT Şanlıurfa Bölge Bayi" />
      <meta itemProp="telephone" content={COMPANY.phone} />
      <meta itemProp="email" content={COMPANY.email} />
      <meta itemProp="url" content={COMPANY.siteUrl} />
      <div className="container-shell">
        {/* SEO-rich description */}
        <div className="mb-12 rounded-2xl bg-white/60 p-6 text-sm leading-relaxed text-ink/55 backdrop-blur-sm lg:p-8">
          <h3 className="mb-3 text-base font-bold text-ink">AKINSOFT Şanlıurfa Bölge Bayi — TEKNO ŞURA</h3>
          <p>
            <strong>AKINSOFT Şanlıurfa Bölge Bayisi</strong> olarak TEKNO ŞURA, Şanlıurfa ve çevre illerde
            işletmelere <strong>Wolvox ERP</strong>, ön muhasebe, <strong>POS sistemleri</strong>, restoran otomasyonu,
            <strong> e-Fatura</strong>, e-Arşiv, stok takip, cari hesap yönetimi ve bulut tabanlı yazılım çözümleri
            sunmaktadır. <strong>AKINSOFT yetkili bayisi</strong> olarak kurulum, eğitim, teknik destek ve güncelleme
            hizmetleriyle işletmenizin dijital dönüşüm yolculuğunda yanınızdayız. Şanlıurfa'da <strong>AKINSOFT
            yazılım</strong> çözümleri için güvenilir adres.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-12 pb-16 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <img src="/logo.png" alt="TEKNO ŞURA — AKINSOFT Şanlıurfa Bölge Bayi" className="h-12 w-auto" itemProp="image" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-ink/60">
              {COMPANY.subtitle}. İşletmenizin dijital dönüşüm ortağı. Wolvox ERP, e-Fatura, POS ve daha fazlası.
            </p>
            <div className="mt-6 flex gap-2.5">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-white text-ink/60 transition-colors duration-300 hover:border-orange hover:bg-orange hover:text-white"
                  data-testid={`footer-social-${s.label}`}
                >
                  <s.icon className="h-4.5 w-4.5" strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          {/* Menu */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-ink">Kurumsal</h4>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-sm text-ink/60 transition-colors hover:text-orange"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-ink">Hizmetler</h4>
            <ul className="mt-5 space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.title}>
                  <a
                    href="#hizmetler"
                    className="text-sm text-ink/60 transition-colors hover:text-orange"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-ink">İletişim</h4>
            <ul className="mt-5 space-y-4">
              <li className="flex items-start gap-3 text-sm text-ink/60">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange" strokeWidth={1.6} />
                {COMPANY.address}
              </li>
              <li>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="flex items-center gap-3 text-sm text-ink/60 transition-colors hover:text-orange"
                >
                  <Phone className="h-4 w-4 shrink-0 text-orange" strokeWidth={1.6} />
                  {COMPANY.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-3 text-sm text-ink/60 transition-colors hover:text-orange"
                >
                  <Mail className="h-4 w-4 shrink-0 text-orange" strokeWidth={1.6} />
                  {COMPANY.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-ink/10 py-8 sm:flex-row">
          <p className="text-sm text-ink/50">
            © {new Date().getFullYear()} {COMPANY.name}. Tüm hakları saklıdır.
          </p>
          <p className="text-sm text-ink/50">{COMPANY.subtitle}</p>
        </div>

        {/* Hidden SEO links for crawlers */}
        <nav className="sr-only" aria-label="SEO bağlantıları">
          <a href="https://teknosura.com">AKINSOFT Şanlıurfa</a>
          <a href="https://www.akinssoftsanliurfabolge.com">AKINSOFT Şanlıurfa Bölge Bayi</a>
          <a href="https://teknosura.com/#yazilimlar">Wolvox ERP Şanlıurfa</a>
          <a href="https://teknosura.com/#hizmetler">AKINSOFT Hizmetler</a>
          <a href="https://teknosura.com/#iletisim">AKINSOFT Şanlıurfa İletişim</a>
        </nav>
      </div>
    </footer>
  );
}
